import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { useCallback, useState } from 'react'
import { youtubePreviewImage } from 'shared-code/string'
import { BasicStyledComponent, DSNCEmbedYoutube } from 'shared-definitions/types'
import Play from 'shared-svg/PlayTrack.svg'
import styles from './NCEmbedYoutube.module.css'

/**
 * Facade first, player on demand.
 *
 * The article body is server-rendered, so the poster (a static YouTube thumbnail plus a
 * play button) is in the initial HTML and paints with the text. react-player and the
 * YouTube iframe chain (iframe_api → widget api → embed document → player bundle) are only
 * fetched once the reader clicks — before that nothing from youtube.com runs on the page.
 */
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false })

interface NCEmbedYoutubeProps extends BasicStyledComponent {
  data: DSNCEmbedYoutube
  /** Position in the body; the first block is the article hero and gets an eager poster. */
  index: number
}

const WARM_ORIGINS = ['https://www.youtube.com', 'https://www.google.com']
let warmed = false

/** Open the connections the player will need, on intent (hover/focus), once per page. */
function warmConnections(): void {
  if (warmed || typeof document === 'undefined') {
    return
  }
  warmed = true
  for (const href of WARM_ORIGINS) {
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = href
    document.head.appendChild(link)
  }
}

const NCEmbedYoutube: React.VFC<NCEmbedYoutubeProps> = ({ data, index, className }) => {
  const [activated, setActivated] = useState(false)
  const [ready, setReady] = useState(false)
  // Mirrors the player's real state. react-player re-applies `playing` on EVERY render
  // (its effect has no dependency list and calls play() whenever the element is paused
  // and playing === true), so a constant `playing` would restart a paused or ended video
  // each time the article body re-renders — e.g. while scrolling.
  const [playing, setPlaying] = useState(false)
  const { youtubeId } = data.video
  const activate = useCallback(() => {
    setActivated(true)
    setPlaying(true)
  }, [])
  const markReady = useCallback(() => setReady(true), [])
  const onPlay = useCallback(() => setPlaying(true), [])
  // The YouTube element dispatches `pause` for both the paused and the ended states.
  const onPause = useCallback(() => setPlaying(false), [])

  return (
    <div className={clsx(className, styles.container)}>
      <img
        className={styles.poster}
        src={youtubePreviewImage(youtubeId, 'hqdefault')}
        alt=""
        width={480}
        height={360}
        loading={index === 0 ? 'eager' : 'lazy'}
        decoding="async"
      />
      {activated ? (
        <>
          {ready ? null : <span className={styles.loading} aria-hidden="true" />}
          <ReactPlayer
            width="100%"
            height="100%"
            className={styles.player}
            controls
            playing={playing}
            src={`https://www.youtube.com/watch?v=${youtubeId}`}
            onReady={markReady}
            onPlay={onPlay}
            onPause={onPause}
          />
        </>
      ) : (
        <button
          type="button"
          className={styles.play}
          aria-label="Play video"
          onClick={activate}
          onPointerEnter={warmConnections}
          onFocus={warmConnections}
        >
          <span className={styles.playBadge}>
            <Play className={styles.playIcon} />
          </span>
        </button>
      )}
    </div>
  )
}

export default NCEmbedYoutube
